import React, { ReactNode, useEffect } from 'react';
import {
  Box,
  ContentTemplate,
  Grid,
  KeyValue,
  MUIDataTable,
  Pill,
  Progress,
  TableActionTypes,
  Typography,
} from 'web-library';
import { useTranslation } from 'react-i18next';
import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import {
  rowsPerPageOptionsDefault,
  useCoordinatorSummaryDataTable,
} from '../../logic/useWithdrawalDataTable';
import {
  useGetWithdrawalsSummary,
  usePostSearchWithdrawalCandidates,
} from '../../../../api/candidate/exam/@coordinator/queries-services/queries';
import { stringToLongDateAndShortTime } from '../../../../utils/DateUtils';
import { ApplicationStatus } from '../../../../api/candidate/exam/model';

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()((theme) =>
({
  marginBottom4: {
    marginBottom: theme.spacing(4),
  },
  marginLeft4: {
    marginLeft: theme.spacing(4),
  },
  selectedCandidates: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(4),
  },
}));

interface SummaryContentProps {
  uuid: string;
  sectionHeader: ReactNode;
  setError: (error: string) => void;
  isView?: boolean;
  setStatusError?: (statusError: boolean) => void;
}

const SummaryContent = ({
  uuid,
  sectionHeader,
  setError,
  isView = false,
  setStatusError,
}: SummaryContentProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const theme = useTheme();
  const { tableState, tableDispatch, tableColumns } = useCoordinatorSummaryDataTable(isView);

  const { mutate: mutatePostCandidates } = usePostSearchWithdrawalCandidates(
    ({ data: withdrawalCandidateResult }) => {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: withdrawalCandidateResult.content,
          count: withdrawalCandidateResult.totalElements,
        },
      } as TableActionTypes);
    },
    () => setError(t('withdrawal->>Coordinator.Summary.Content.SelectedCandidateTableError'))
  );

  const { data: groupWithdrawalSummary } = useGetWithdrawalsSummary(uuid, (withdrawalSummary) => {
    if (
      !isView &&
      ApplicationStatus.DRAFT !== withdrawalSummary.applicationStatus &&
      setStatusError
    ) {
      setStatusError(true);
    }

    mutatePostCandidates({
      uuid,
      apiPathParam: {
        page: tableState.page,
        pageSize: tableState.size,
      },
    });
  });

  useEffect(() => {
    mutatePostCandidates({
      uuid,
      apiPathParam: {
        page: tableState.page,
        pageSize: tableState.size,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, tableState.page, tableState.size]);

  if (!groupWithdrawalSummary) {
    return (
      <ContentTemplate transparent continuous>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  return (
    <>
      <ContentTemplate isDividerBottom type="large">
        {sectionHeader}
        <Grid container className={classes.marginBottom4}>
          <Grid md={6} sm={12} xs={12}>
            <KeyValue
              id="applicationId"
              keyText={t('withdrawal->>Coordinator.Summary.Content.ApplicationID')}
              valueText={groupWithdrawalSummary.applicationId}
            />
          </Grid>
          {isView && (
            <>
              <Grid md={6} sm={12} xs={12}>
                <KeyValue
                  id="status"
                  keyText={t('withdrawal->>Coordinator.Summary.View.Status')}
                  valueText={
                    <Pill variant="info" label={groupWithdrawalSummary.applicationStatus} />
                  }
                />
              </Grid>
              <Grid md={6} sm={12} xs={12}>
                <KeyValue
                  id="applicationDateTime"
                  keyText={t('withdrawal->>Coordinator.Summary.Content.ApplicationDateTime')}
                  valueText={stringToLongDateAndShortTime(
                    groupWithdrawalSummary.applicationDateTime
                  )}
                />
              </Grid>
              <Grid md={6} sm={12} xs={12}>
                <KeyValue
                  id="lastUpdatedDateTime"
                  keyText={t('withdrawal->>Coordinator.Summary.Content.LastUpdatedDateTime')}
                  valueText={
                    groupWithdrawalSummary.lastUpdatedDateTime
                      ? stringToLongDateAndShortTime(groupWithdrawalSummary.lastUpdatedDateTime)
                      : '-'
                  }
                />
              </Grid>
            </>
          )}
          <Grid md={12} sm={12} xs={12}>
            <KeyValue
              id="coordinator"
              keyText={t('withdrawal->>Coordinator.Summary.Content.Coordinator')}
              valueText={groupWithdrawalSummary.coordinatorName}
            />
          </Grid>
        </Grid>
      </ContentTemplate>
      <ContentTemplate isDividerBottom type="large" continuous>
        <Box marginBottom={theme.spacing(0.5)}>
          <Typography variant="h3" className={classes.marginBottom4}>
            {t('withdrawal->>Coordinator.Summary.Content.WithdrawalDetails')}
          </Typography>
          <KeyValue
            id="reason"
            keyText={t('withdrawal->>Coordinator.Summary.Content.ReasonForWithdrawal')}
            valueText={groupWithdrawalSummary.reasonForWithdrawal}
            capitalize={false}
          />
          {groupWithdrawalSummary.specifiedReason && (
            <KeyValue
              id="examYear"
              keyText={t('withdrawal->>Coordinator.Summary.Content.SpecifiedReason')}
              valueText={groupWithdrawalSummary.specifiedReason}
              capitalize={false}
            />
          )}
        </Box>
      </ContentTemplate>
      {groupWithdrawalSummary && (
        <ContentTemplate type="full" continuous>
          <Box className={classes.selectedCandidates}>
            <Typography variant="h3">
              {t('withdrawal->>Coordinator.Summary.Content.SelectedCandidates')}
            </Typography>
            <Pill
              label={tableState.data.length.toString()}
              variant="info"
              className={classes.marginLeft4}
            />
          </Box>
          <KeyValue
            id="examYear"
            keyText={t('withdrawal->>Coordinator.Summary.Content.ExamYear')}
            valueText={groupWithdrawalSummary.examYear}
          />
          <MUIDataTable
            state={tableState}
            dispatch={tableDispatch}
            columns={tableColumns}
            data={tableState.data}
            options={{
              pagination: true,
            }}
            emptyDataLabel={t('withdrawal->>Coordinator.SelectCandidates.EmptyDataTableContent')}
            rowsPerPageOptions={rowsPerPageOptionsDefault}
          />
        </ContentTemplate>
      )}
    </>
  );
};

export default SummaryContent;
